import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/shadcn/input';
import { Card, CardContent, CardHeader, CardTitle } from '@shared/components/ui/shadcn/card';
import { Loader2 } from 'lucide-react';
import { useToast } from '@shared/hooks/use-toast';

interface ScannerInputProps {
  stepDescription: string;
  placeholder?: string;
  buttonText?: string;
  onScan: (code: string) => Promise<{ success: boolean; message: string }>;
  disabled?: boolean;
  /** Motivo cuando el escáner está deshabilitado (ej. falta courier) */
  disabledReason?: string;
}

export const ScannerInput: React.FC<ScannerInputProps> = ({
  stepDescription,
  placeholder = 'Escanea o escribe el código...',
  buttonText = 'Confirmar',
  onScan,
  disabled = false,
  disabledReason,
}) => {
  const { toast } = useToast();
  const [inputValue, setInputValue] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = useCallback(
    async (code: string) => {
      if (!code.trim() || isProcessing || disabled) return;

      setIsProcessing(true);

      try {
        const result = await onScan(code.trim());

        toast({
          title: result.success ? 'Éxito' : 'Error',
          description: result.message,
          variant: result.success ? 'default' : 'destructive',
        });

        // Limpiar input siempre
        setInputValue('');

        // Enfocar el input para el siguiente escaneo
        setTimeout(() => {
          inputRef.current?.focus();
        }, 100);
      } catch {
        toast({
          title: 'Error',
          description: 'Error al procesar el código',
          variant: 'destructive',
        });
        setInputValue('');
        setTimeout(() => {
          inputRef.current?.focus();
        }, 100);
      } finally {
        setIsProcessing(false);
      }
    },
    [onScan, isProcessing, disabled, toast],
  );

  // Auto-focus inicial
  useEffect(() => {
    if (disabled) return;

    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [disabled]);

  // Limpiar el input al cambiar de paso/placeholder para evitar leyendas desfasadas
  useEffect(() => {
    setInputValue('');
  }, [placeholder, stepDescription]);

  // Capturar teclado globalmente
  useEffect(() => {
    if (disabled || isProcessing) return;

    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      const currentHasFocus = inputRef.current === document.activeElement;

      // Verificar si estamos en un modal o input importante
      const activeElement = document.activeElement;
      const isInModal = activeElement?.closest('[role="dialog"], .modal, .popup');
      const isTypingInImportantInput =
        activeElement &&
        (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA') &&
        activeElement !== inputRef.current &&
        (activeElement.getAttribute('type') === 'password' ||
          activeElement.getAttribute('type') === 'email' ||
          activeElement.getAttribute('type') === 'search' ||
          activeElement.closest('form') ||
          isInModal);

      if (isTypingInImportantInput) {
        return;
      }

      // Solo ignorar modificadores importantes
      if (e.ctrlKey || e.metaKey || e.altKey) {
        return;
      }

      // Capturar Enter
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSubmit(inputValue);
        return;
      }

      // Capturar Escape para limpiar
      if (e.key === 'Escape') {
        e.preventDefault();
        setInputValue('');
        return;
      }

      // Capturar caracteres válidos
      if (e.key.length === 1 && /[a-zA-Z0-9\-_.\s/\\@#$%&*+=()[\]{}|:;"'<>,?!~`]/.test(e.key)) {
        e.preventDefault();

        if (!currentHasFocus && inputRef.current) {
          inputRef.current.focus();
        }

        setInputValue((prev) => prev + e.key);
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);

    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown);
    };
  }, [disabled, isProcessing, inputValue, handleSubmit]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit(inputValue);
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSubmit(inputValue);
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-xl text-center">Escáner de Códigos</CardTitle>
        <p className="text-sm text-muted-foreground text-center">{stepDescription}</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Formulario de scanner */}
        <form onSubmit={handleFormSubmit} className="space-y-4">
          <div className="relative">
            <Input
              ref={inputRef}
              type="text"
              placeholder={placeholder}
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              disabled={disabled || isProcessing}
              className="h-12 text-center text-lg"
              autoComplete="off"
              autoFocus
            />

            {/* Indicador de procesando */}
            {isProcessing && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
              </div>
            )}
          </div>

          {disabled && disabledReason && <p className="text-sm text-center text-muted-foreground">{disabledReason}</p>}

          {/* Botón de enviar */}
          <Button
            type="submit"
            variant="primary"
            size="lg"
            className="w-full"
            disabled={!inputValue.trim() || disabled || isProcessing}
          >
            {isProcessing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Procesando...
              </>
            ) : (
              buttonText
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};
